import * as React from 'react';
import {useDesignerSelector} from '@packages/systems/designer/flux';
import {selectSiteId} from '@packages/systems/designer/site-data';
import {reportBug} from '@packages/utilities/reporter';

export type SiteFormDefaultsData = {
  emailSenderName: string;
  emailDestinations: string[];
  emailSubject: string;
  emailReplyTo: string;
  emailFormTemplate: string;
};

/**
 * Returns a memoized callback that fetches the site-level form defaults from
 * the server. Centralizes the request so callers don't reach for `fetch`
 * directly and any future migration to a shared HTTP client touches one place.
 */
export const useSiteFormDefaults = () => {
  const siteId = useDesignerSelector(selectSiteId);

  return React.useCallback(async (): Promise<SiteFormDefaultsData | null> => {
    const response = await fetch(`/api/v1/site/${siteId}/form/defaults`, {
      method: 'GET',
      credentials: 'same-origin',
    });
    // 404 means the site has no defaults configured yet, which is expected.
    if (response.status === 404) {
      return null;
    }
    if (!response.ok) {
      const error = new Error(
        `Failed to fetch site form defaults: ${response.status}`
      );
      reportBug('[useSiteFormDefaults]', error.message, {
        team: 'developer-platform',
        ticket: 'https://webflow.atlassian.net/browse/DEVPL-3599',
        status: response.status,
      });
      throw error;
    }
    return response.json();
  }, [siteId]);
};
